import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@one-thd/sui-atomic-components';
import {
  getRefinementRangeAndUrl,
  hasComparator,
  sortRefinements
} from '../../../product-results-helpers';

import NumericRangeInput from './NumericRangeInput';

const NumericRangeRefinement = (props) => {
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [sortedRefinements, setSortedRefinements] = useState([]);

  const [openMinValueList, setOpenMinValueList] = useState(false);
  const minValueRef = useRef(null);

  const [openMaxValueList, setOpenMaxValueList] = useState(false);
  const maxValueRef = useRef(null);

  const {
    dimension,
    metadata,
    onChange
  } = props;

  const isButtonDisabled = useCallback(() => {
    const hasMinValue = minValue.length > 0;
    const hasMaxValue = maxValue.length > 0;
    const isAnyEmpty = !hasMinValue || !hasMaxValue;
    const isMaxZero = maxValue === '0';
    const areBothZero = minValue === '0' && maxValue === '0';

    return isAnyEmpty || isMaxZero || areBothZero;
  }, [minValue, maxValue]);

  const clearInputFields = () => {
    setMinValue('');
    setMaxValue('');
  };

  const handleScroll = (name) => {
    if (name === 'numeric-filter-minValue') {
      setOpenMinValueList(false);
    } else {
      setOpenMaxValueList(false);
    }
  };

  const handleOnApplyRange = useCallback((event) => {
    event.preventDefault();
    if (!isButtonDisabled()) {

      // default upper and lowerbound values to the lower and upper ranges of refinement label when a user did not
      // input a custom value
      const lowerBoundValue = minValue || 0;

      const upperBoundValue = maxValue;

      const { refinements, url } = getRefinementRangeAndUrl({
        refinements: dimension.refinements,
        lowerBound: lowerBoundValue,
        metadata,
        upperBound: upperBoundValue
      });

      let refinementUrl;
      /* istanbul ignore next */
      if (refinements.every((el) => typeof el === 'string')) {
        // get separate url paths
        let paramsArr = url.split('/');
        // find index of path with nValue
        const index = paramsArr.findIndex((el) => el.includes('N-5yc1v'));
        // modify nValue
        const [navParam, ...keys] = paramsArr[index].split('?');
        let nValue = navParam + refinements.join('');
        // append keys (ie. lowerBound/upperBound) if present
        nValue = keys.length > 0 ? nValue + '?' + keys : nValue;
        paramsArr[index] = nValue;
        // create new url
        refinementUrl = paramsArr.join('/');
      } else {
        refinementUrl = url;
      }

      if (onChange) {
        onChange({
          refinement: {
            url: refinementUrl,
            deselectUrl: '?'
          },
          dimension: {
            dimensionId: 'custom_refinement',
            label: dimension.label
          },
          refinementRange: refinements
        }
        );
      }
      clearInputFields();
    }
  }, [
    minValue,
    maxValue,
    isButtonDisabled,
    onChange,
    metadata,
    dimension
  ]
  );

  const onFocusMinNum = useCallback((_event) => {
    setOpenMinValueList((prev) => !prev);
  }, []);

  const onFocusMaxNum = useCallback((_event) => {
    setOpenMaxValueList((prev) => !prev);
  }, []);

  const handleOnBlur = useCallback((_event) => {
    setOpenMinValueList(false);
    setOpenMaxValueList(false);
  }, []);

  const extractNumericValue = (label) => {
    return label.split(' ')[0].replace(/[^\d.]/g, '');
  };

  const handleMinNumValue = (evt) => {
    const value = extractNumericValue((evt.target.value || ''));
    setMinValue(value);
  };

  const handleMaxNumValue = (evt) => {
    const value = extractNumericValue((evt.target.value || ''));
    setMaxValue(value);
  };

  const handleMinDropdownClick = useCallback((event) => {
    event.preventDefault();
    const value = extractNumericValue(event.target.innerText);
    setMinValue(value);
    handleScroll('numeric-filter-minValue');
  }, []);

  const handleMaxDropdownClick = useCallback((event) => {
    event.preventDefault();
    const value = extractNumericValue(event.target.innerText);
    setMaxValue(value);
    handleOnBlur(event);
  }, []);

  useEffect(() => {
    let _refinements = dimension?.refinements.filter((ref) => {
      return ref.label && !hasComparator(ref);
    });

    const _sortedRefinements = _refinements.sort(sortRefinements);
    setSortedRefinements(_sortedRefinements);
  }, []);

  return (
    <form
      data-testid="numeric-range-refinement-form"
      className="sui-flex sui-mb-5"
      autoComplete="off"
      noValidate
    >

      <NumericRangeInput
        id="numeric-filter-minValue"
        data-testid="numeric-filter-minValue"
        placeholder="Min"
        name="numeric-filter-minValue"
        value={minValue}
        onChange={handleMinNumValue}
        onFocus={onFocusMinNum}
        onBlur={handleOnBlur}
        open={openMinValueList}
        anchorRef={minValueRef}
        onScrollDropDown={handleScroll}
        onClickOfDropDown={handleMinDropdownClick}
        items={sortedRefinements}
        minValue={minValue}
      />

      <span className="sui-px-4 sui-flex sui-items-center">to</span>

      <NumericRangeInput
        id="numeric-filter-maxValue"
        data-testid="numeric-filter-maxValue"
        placeholder="Max"
        name="numeric-filter-maxValue"
        value={maxValue}
        onChange={handleMaxNumValue}
        onFocus={onFocusMaxNum}
        onBlur={handleOnBlur}
        open={openMaxValueList}
        anchorRef={maxValueRef}
        onScrollDropDown={handleScroll}
        onClickOfDropDown={handleMaxDropdownClick}
        items={sortedRefinements}
        minValue={minValue}
        isMaxValue
      />

      <div className="sui-ml-4">
        <Button
          variant="tertiary"
          disabled={isButtonDisabled()}
          onClick={handleOnApplyRange}
          data-testid="custom-numericRange-applyBtn"
        >
          Apply
        </Button>
      </div>
    </form>
  );
};
NumericRangeRefinement.propTypes = {
  dimension: PropTypes.shape({
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    ),
    label: PropTypes.string
  }),
  metadata: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired
};

NumericRangeRefinement.defaultProps = {
  dimension: null
};

NumericRangeRefinement.displayName = 'NumericRangeRefinement';

export { NumericRangeRefinement };